/**
 * ErrorBoundary — captura erros de render e mostra a mensagem na tela
 * (em vez de tela preta). Usado para envolver rotas pesadas como /chat.
 *
 * Em produção, um erro de render não tratado desmonta a árvore e deixa só
 * o fundo — o que parece "tela em branco". Este boundary transforma isso
 * num cartão legível com a mensagem, ajudando o diagnóstico no mobile.
 */
import React from 'react';

/**
 * Erro de chunk = build trocado sob os pés do usuário.
 *
 * Cada deploy gera novos hashes nos arquivos de /static. Quem estava com o app
 * aberto continua rodando o main.js antigo, que ao abrir uma tela carregada sob
 * demanda pede um chunk que não existe mais no Hosting → 404 → tela de erro.
 * Não é bug da tela que o usuário abriu; é versão velha em memória.
 */
const isChunkError = (error) => {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = error.message || String(error);
  return /Loading chunk \S+ failed|Loading CSS chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(msg);
};

// A chave inclui a mensagem (que carrega o nome do chunk): um deploy novo gera
// chunk diferente e ganha direito a mais um recarregamento automático, enquanto
// o MESMO chunk falhando de novo nunca entra em laço de reload.
const reloadKey = (error) => `iat:chunk-reload:${(error && error.message) || 'desconhecido'}`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, staleBuild: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Loga no console pra inspeção via devtools/remote.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render error:', error, info?.componentStack);

    if (isChunkError(error)) {
      const chave = reloadKey(error);
      let jaTentou = false;
      try { jaTentou = sessionStorage.getItem(chave) === '1'; } catch { /* modo privado */ }
      if (!jaTentou) {
        try { sessionStorage.setItem(chave, '1'); } catch { /* modo privado */ }
        // index.html é no-cache no Hosting, então o reload traz o bundle novo.
        window.location.reload();
        return;
      }
      this.setState({ staleBuild: true, info });
      return;
    }

    this.setState({ info });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, info, staleBuild } = this.state;
    const msg = staleBuild
      ? 'Uma versão nova do app foi publicada enquanto esta aba estava aberta. Recarregue para continuar.'
      : ((error && (error.message || String(error))) || 'Erro desconhecido');
    // Stack só ajuda em erro de verdade; em build trocado é ruído que assusta.
    const stack = staleBuild ? '' : (info?.componentStack || (error && error.stack) || '');

    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#1A1A20',
          color: '#FAFAFB',
          padding: '24px 16px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          boxSizing: 'border-box',
        }}
      >
        <h2 style={{ color: '#FFC107', fontSize: 18, margin: '0 0 12px' }}>
          {staleBuild ? 'Atualização disponível' : 'Algo quebrou ao abrir esta tela'}
        </h2>
        <p style={{ fontSize: 14, color: '#C4C4CC', margin: '0 0 16px' }}>
          {msg}
        </p>
        {stack ? (
        <pre
          style={{
            fontSize: 11,
            lineHeight: 1.4,
            color: '#9A9AA5',
            background: '#0E0E12',
            border: '1px solid #3A3A44',
            borderRadius: 8,
            padding: 12,
            overflow: 'auto',
            maxHeight: '40vh',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {stack}
        </pre>
        ) : null}
        <button
          type="button"
          onClick={() => { window.location.reload(); }}
          style={{
            marginTop: 16,
            background: '#FFC107',
            color: '#0E0E12',
            border: 'none',
            borderRadius: 9999,
            padding: '10px 20px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Recarregar
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
